const CLICK_ID_PARAMS = [
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'msclkid',
  'rclid'
] as const;
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const STORAGE_KEY = 'layers_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface AttributionData {
  click_id_param?: string;
  click_id_value?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_url?: string;
  captured_at: number;
}

/**
 * Capture attribution signals from the current page URL and referrer.
 * Persists to localStorage with a 30-day TTL. Click IDs take priority:
 * if a new click ID is present, the entire record is overwritten.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  // Check for click IDs first — any click ID overwrites the stored record
  let clickIdParam: string | undefined;
  let clickIdValue: string | undefined;
  for (const param of CLICK_ID_PARAMS) {
    const value = params.get(param);
    if (value) {
      clickIdParam = param;
      clickIdValue = value;
      break; // first match wins
    }
  }

  // Collect UTM params
  const utms: Partial<Pick<AttributionData, (typeof UTM_PARAMS)[number]>> = {};
  let hasUtm = false;
  for (const param of UTM_PARAMS) {
    const value = params.get(param);
    if (value) {
      utms[param] = value;
      hasUtm = true;
    }
  }

  const referrer =
    typeof document !== 'undefined' && document.referrer ? document.referrer : undefined;

  // Nothing to capture
  if (!clickIdParam && !hasUtm && !referrer) return;

  const existing = getAttribution();

  // If a new click ID is present, overwrite entirely
  if (clickIdParam) {
    const data: AttributionData = {
      click_id_param: clickIdParam,
      ...(clickIdValue != null && { click_id_value: clickIdValue }),
      ...utms,
      ...(referrer != null && { referrer_url: referrer }),
      captured_at: Date.now()
    };
    writeAttribution(data);
    return;
  }

  // If UTMs are present, overwrite (fresh campaign visit)
  if (hasUtm) {
    const data: AttributionData = {
      // Preserve existing click ID if no new one
      ...(existing?.click_id_param != null && { click_id_param: existing.click_id_param }),
      ...(existing?.click_id_value != null && { click_id_value: existing.click_id_value }),
      ...utms,
      ...(referrer != null && { referrer_url: referrer }),
      captured_at: Date.now()
    };
    writeAttribution(data);
    return;
  }

  // Only referrer and no existing attribution — store it
  if (!existing) {
    const data: AttributionData = {
      ...(referrer != null && { referrer_url: referrer }),
      captured_at: Date.now()
    };
    writeAttribution(data);
  }
}

/**
 * Read stored attribution data, returning null if missing or expired.
 */
export function getAttribution(): AttributionData | null {
  if (typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data: AttributionData = JSON.parse(raw);
    if (Date.now() - data.captured_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Return a flat property bag suitable for merging into event properties.
 * Keys are prefixed with `$attribution_` to avoid collisions.
 */
export function getAttributionProperties(): Record<string, string> {
  const data = getAttribution();
  if (!data) return {};

  const props: Record<string, string> = {};

  if (data.click_id_param) props['$attribution_click_id_param'] = data.click_id_param;
  if (data.click_id_value) props['$attribution_click_id_value'] = data.click_id_value;
  if (data.utm_source) props['$attribution_utm_source'] = data.utm_source;
  if (data.utm_medium) props['$attribution_utm_medium'] = data.utm_medium;
  if (data.utm_campaign) props['$attribution_utm_campaign'] = data.utm_campaign;
  if (data.utm_content) props['$attribution_utm_content'] = data.utm_content;
  if (data.utm_term) props['$attribution_utm_term'] = data.utm_term;
  if (data.referrer_url) props['$attribution_referrer_url'] = data.referrer_url;

  return props;
}

function writeAttribution(data: AttributionData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}
